import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import logger from '@/lib/logger';

function LicensesTab({ projectId, user }) {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [formData, setFormData] = useState({
    license_name: '',
    license_number: '',
    issuing_authority: '',
    issue_date: '',
    expiration_date: '',
    notes: '',
  });

  useEffect(() => {
    fetchLicenses();
  }, [projectId]);

  const fetchLicenses = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/licenses`);
      setLicenses(response.data);
    } catch (error) {
      logger.error('Error fetching licenses:', error);
      toast.error('Erro ao carregar licenças');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLicense) {
        await api.put(`/licenses/${editingLicense.license_id}`, formData);
        toast.success('Licença atualizada com sucesso!');
      } else {
        await api.post(`/projects/${projectId}/licenses`, formData);
        toast.success('Licença criada com sucesso!');
      }
      setOpenDialog(false);
      setEditingLicense(null);
      setFormData({
        license_name: '',
        license_number: '',
        issuing_authority: '',
        issue_date: '',
        expiration_date: '',
        notes: '',
      });
      fetchLicenses();
    } catch (error) {
      logger.error('Error saving license:', error);
      toast.error('Erro ao salvar licença');
    }
  };

  const handleEdit = (license) => {
    setEditingLicense(license);
    setFormData({
      license_name: license.license_name,
      license_number: license.license_number,
      issuing_authority: license.issuing_authority,
      issue_date: license.issue_date,
      expiration_date: license.expiration_date,
      notes: license.notes || '',
    });
    setOpenDialog(true);
  };

  const handleDelete = async (licenseId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta licença?')) return;

    try {
      await api.delete(`/licenses/${licenseId}`);
      toast.success('Licença excluída com sucesso!');
      fetchLicenses();
    } catch (error) {
      logger.error('Error deleting license:', error);
      toast.error('Erro ao excluir licença');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      valid: 'bg-green-100 text-green-700 border-green-300',
      expiring: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      critical: 'bg-orange-100 text-orange-700 border-orange-300',
      expired: 'bg-red-100 text-red-700 border-red-300',
    };
    const labels = {
      valid: 'Válida',
      expiring: 'Expirando (30-90d)',
      critical: 'Crítica (<30d)',
      expired: 'Expirada',
    };
    return (
      <Badge className={`border ${styles[status] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Licenças Ambientais</h2>
        {(user?.role === 'ADMIN' || user?.role === 'CONSULTANT') && (
          <Dialog
            open={openDialog}
            onOpenChange={(open) => {
              setOpenDialog(open);
              if (!open) {
                setEditingLicense(null);
                setFormData({
                  license_name: '',
                  license_number: '',
                  issuing_authority: '',
                  issue_date: '',
                  expiration_date: '',
                  notes: '',
                });
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="bg-emerald-800 hover:bg-emerald-900" data-testid="add-license-button">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Adicionar Licença
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingLicense ? 'Editar Licença' : 'Nova Licença'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="license_name">Nome da Licença</Label>
                  <Input
                    id="license_name"
                    data-testid="license-name-input"
                    value={formData.license_name}
                    onChange={(e) => setFormData({ ...formData, license_name: e.target.value })}
                    placeholder="Ex: Licença de Operação"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="license_number">Número da Licença</Label>
                    <Input
                      id="license_number"
                      data-testid="license-number-input"
                      value={formData.license_number}
                      onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                      placeholder="Ex: LO-12345/2025"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="issuing_authority">Órgão Emissor</Label>
                    <Input
                      id="issuing_authority"
                      data-testid="issuing-authority-input"
                      value={formData.issuing_authority}
                      onChange={(e) => setFormData({ ...formData, issuing_authority: e.target.value })}
                      placeholder="Ex: IBAMA"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="issue_date">Data de Emissão</Label>
                    <Input
                      id="issue_date"
                      type="date"
                      data-testid="issue-date-input"
                      value={formData.issue_date}
                      onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="expiration_date">Data de Vencimento</Label>
                    <Input
                      id="expiration_date"
                      type="date"
                      data-testid="expiration-date-input"
                      value={formData.expiration_date}
                      onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes">Observações (opcional)</Label>
                  <Textarea
                    id="notes"
                    data-testid="notes-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Informações adicionais sobre a licença"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900" data-testid="submit-license-button">
                    {editingLicense ? 'Atualizar' : 'Criar'} Licença
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4">
        {licenses.length === 0 ? (
          <Card className="p-12 border border-gray-200 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-gray-500">Nenhuma licença cadastrada para este projeto</p>
          </Card>
        ) : (
          licenses.map((license) => {
            const expirationDate = new Date(license.expiration_date);
            const today = new Date();
            const daysUntil = Math.floor((expirationDate - today) / (1000 * 60 * 60 * 24));

            return (
              <Card
                key={license.license_id}
                className={`p-6 border-2 ${
                  license.status === 'expired'
                    ? 'border-red-300 bg-red-50'
                    : license.status === 'critical'
                    ? 'border-orange-300 bg-orange-50'
                    : license.status === 'expiring'
                    ? 'border-yellow-300 bg-yellow-50'
                    : 'border-green-300 bg-green-50'
                }`}
                data-testid={`license-card-${license.license_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">{license.license_name}</h3>
                      {getStatusBadge(license.status)}
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Número:</p>
                        <p className="font-medium text-gray-900">{license.license_number}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Órgão Emissor:</p>
                        <p className="font-medium text-gray-900">{license.issuing_authority}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Data de Emissão:</p>
                        <p className="font-medium text-gray-900">
                          {new Date(license.issue_date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Data de Vencimento:</p>
                        <p
                          className={`font-semibold ${
                            license.status === 'expired'
                              ? 'text-red-700'
                              : license.status === 'critical'
                              ? 'text-orange-700'
                              : license.status === 'expiring'
                              ? 'text-yellow-700'
                              : 'text-green-700'
                          }`}
                        >
                          {new Date(license.expiration_date).toLocaleDateString('pt-BR')}
                          {daysUntil < 0 && ` (${Math.abs(daysUntil)}d atrasado)`}
                          {daysUntil >= 0 && ` (${daysUntil}d restantes)`}
                        </p>
                      </div>
                    </div>

                    {license.notes && (
                      <div className="mt-4">
                        <p className="text-gray-600 text-sm">Observações:</p>
                        <p className="text-gray-900 text-sm mt-1">{license.notes}</p>
                      </div>
                    )}
                  </div>

                  {(user?.role === 'ADMIN' || user?.role === 'CONSULTANT') && (
                    <div className="flex space-x-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(license)}
                        data-testid={`edit-license-${license.license_id}`}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDelete(license.license_id)}
                        data-testid={`delete-license-${license.license_id}`}
                      >
                        Excluir
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}

export default LicensesTab;